/**
 * GatherVoices support chatbot — floating widget.
 *
 * Behavior:
 * - Fixed bottom-right on every route.
 * - Fetches page context from URL: memorial routes (/t/:slug, /watch/:slug on
 *   memorial events) get a gentler tone via `context.event_type == 'memorial'`.
 * - Multi-turn: client owns the history and re-sends it each turn.
 * - Escalation footer: mailto to support@gathervoices.io with a prefilled subject.
 */

import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { MessageCircle, X, Send, Sparkles, Mail, Loader2 } from "lucide-react";
import api from "@/lib/api";

const SUPPORT_EMAIL = "support@gathervoices.io";
const STORAGE_KEY = "gathervoices_support_session";
const HISTORY_KEY = "gathervoices_support_history";

const SUGGESTED_QUESTIONS = [
  "How much does it cost?",
  "How long is my film kept?",
  "Can I extend my deadline?",
  "Is my event private?",
];

function loadHistory() {
  try {
    const raw = sessionStorage.getItem(HISTORY_KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr : [];
  } catch { return []; }
}

function saveHistory(h) {
  try { sessionStorage.setItem(HISTORY_KEY, JSON.stringify(h.slice(-24))); } catch {}
}

function loadSessionId() {
  try { return sessionStorage.getItem(STORAGE_KEY) || null; } catch { return null; }
}
function saveSessionId(sid) {
  try { if (sid) sessionStorage.setItem(STORAGE_KEY, sid); } catch {}
}

export default function SupportChat() {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [pageContext, setPageContext] = useState(null); // { event_type, slug }
  const [showHandoff, setShowHandoff] = useState(false);
  const [handoffEmail, setHandoffEmail] = useState("");
  const [handoffNote, setHandoffNote] = useState("");
  const [handoffSending, setHandoffSending] = useState(false);
  const [handoffTicket, setHandoffTicket] = useState(null);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  // Hydrate persisted state once on mount.
  useEffect(() => {
    setMessages(loadHistory());
    setSessionId(loadSessionId());
    // Prefill handoff email from current session if logged in (silent fail on public pages).
    api.get("/auth/me").then((r) => {
      if (r?.data?.email) setHandoffEmail(r.data.email);
    }).catch(() => {});
  }, []);

  // Detect page context (memorial-safe tone).
  useEffect(() => {
    let alive = true;
    const parts = location.pathname.split("/").filter(Boolean);
    const slug = (parts[0] === "t" || parts[0] === "watch") ? parts[1] : null;
    if (!slug) { setPageContext(null); return; }
    // Try tribute endpoint first (public), then watch endpoint.
    (async () => {
      try {
        const res = await api.get(`/tribute/${slug}`);
        if (alive) setPageContext({ event_type: res.data?.type, slug });
      } catch {
        try {
          const res = await api.get(`/watch/${slug}`);
          if (alive) setPageContext({ event_type: res.data?.type, slug });
        } catch { if (alive) setPageContext(null); }
      }
    })();
    return () => { alive = false; };
  }, [location.pathname]);

  // Auto-scroll to bottom on new messages.
  useEffect(() => {
    if (!open) return;
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, open, sending]);

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 120);
  }, [open]);

  const memorial = pageContext?.event_type === "memorial";
  const persona = useMemo(() => ({
    heading: memorial ? "We're here — gently" : "GatherVoices help",
    sub: memorial ? "Take your time. We usually reply in a moment." : "Usually replies in a moment.",
    greeting: memorial
      ? "Hi. I can help with anything GatherVoices — the tribute page, invitations, film. Ask me anything, whenever you're ready."
      : "Hi! Ask me anything about GatherVoices — pricing, invites, film, or how a feature works. I'm here to help.",
  }), [memorial]);

  const send = async (text) => {
    const value = (text ?? draft).trim();
    if (!value || sending) return;
    setDraft("");
    const nextUser = { role: "user", content: value };
    const nextMessages = [...messages, nextUser];
    setMessages(nextMessages);
    saveHistory(nextMessages);
    setSending(true);
    try {
      // Only send `role` + `content` (plain strings) — server validates Dict[str, str].
      const cleanHistory = nextMessages.slice(-13, -1).map((m) => ({
        role: m.role,
        content: String(m.content || ""),
      }));
      const res = await api.post("/support/chat", {
        message: value,
        session_id: sessionId,
        history: cleanHistory,
        context: pageContext || {},
      });
      const reply = res.data?.reply || `Sorry — please email ${SUPPORT_EMAIL} and we'll help.`;
      const suggest = !!res.data?.suggest_email;
      const sid = res.data?.session_id;
      if (sid && sid !== sessionId) { setSessionId(sid); saveSessionId(sid); }
      const withReply = [...nextMessages, { role: "assistant", content: reply, suggest_email: suggest }];
      setMessages(withReply);
      saveHistory(withReply);
    } catch (e) {
      let detail = e?.response?.data?.detail;
      if (Array.isArray(detail)) detail = detail[0]?.msg || "That message wasn't accepted — try rephrasing.";
      if (typeof detail !== "string") detail = "The helper is briefly unavailable. Please try again in a moment.";
      const withErr = [...nextMessages, { role: "assistant", content: detail, error: true, suggest_email: true }];
      setMessages(withErr);
      saveHistory(withErr);
    } finally { setSending(false); }
  };

  const reset = () => {
    setMessages([]);
    saveHistory([]);
    try { sessionStorage.removeItem(STORAGE_KEY); } catch {}
    setSessionId(null);
  };

  const mailto = `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(
    memorial ? "GatherVoices — a gentle question" : "GatherVoices — quick question"
  )}`;

  const openHandoff = () => {
    setHandoffTicket(null);
    setShowHandoff(true);
  };

  const submitHandoff = async () => {
    const email = handoffEmail.trim();
    if (!email) return;
    setHandoffSending(true);
    try {
      const res = await api.post("/support/handoff", {
        contact_email: email,
        note: handoffNote,
        transcript: messages.map((m) => ({ role: m.role, content: String(m.content || "") })),
        context: pageContext || {},
        page_url: typeof window !== "undefined" ? window.location.href : null,
      });
      setHandoffTicket(res.data?.ticket_id || "received");
    } catch (e) {
      const detail = e?.response?.data?.detail || "Couldn't send just now — please email us directly.";
      window.alert(typeof detail === "string" ? detail : "Couldn't send just now — please email us directly.");
    } finally { setHandoffSending(false); }
  };

  return (
    <>
      {/* Floating bubble */}
      {!open && (
        <button
          data-testid="support-chat-open"
          aria-label="Open support chat"
          onClick={() => setOpen(true)}
          className="fixed bottom-5 right-5 z-[60] h-14 w-14 rounded-full bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] shadow-[0_16px_40px_-12px_rgba(0,0,0,0.5)] grid place-items-center hover:scale-105 transition-transform group"
        >
          <span className="absolute inset-0 rounded-full ring-0 ring-[hsl(var(--accent))]/40 group-hover:ring-4 transition-all" />
          <MessageCircle size={22} />
        </button>
      )}

      {/* Chat panel */}
      {open && (
        <div
          data-testid="support-chat-panel"
          className="fixed bottom-4 right-4 z-[60] w-[min(94vw,380px)] h-[min(78vh,600px)] rounded-2xl bg-[hsl(var(--card))] border border-[hsl(var(--border))] shadow-[0_24px_60px_-12px_rgba(0,0,0,0.35)] flex flex-col overflow-hidden"
        >
          {/* Header */}
          <div className="px-4 pt-4 pb-3 border-b border-[hsl(var(--border))] flex items-start gap-3">
            <div className="h-9 w-9 grid place-items-center rounded-xl shrink-0" style={{ background: "hsl(35 55% 92%)", color: "hsl(35 60% 30%)" }}>
              <Sparkles size={16} />
            </div>
            <div className="min-w-0 flex-1">
              <div className="font-display font-semibold text-sm">{persona.heading}</div>
              <div className="text-[11px] text-[hsl(var(--muted-foreground))] truncate">{persona.sub}</div>
            </div>
            <button data-testid="support-chat-mailto" onClick={openHandoff} className="text-[11px] font-semibold text-[hsl(var(--accent))] hover:underline inline-flex items-center gap-1 shrink-0">
              <Mail size={12} /> Email
            </button>
            <button data-testid="support-chat-close" onClick={() => setOpen(false)} className="h-8 w-8 grid place-items-center rounded-lg hover:bg-[hsl(var(--muted))] text-[hsl(var(--muted-foreground))] shrink-0">
              <X size={16} />
            </button>
          </div>

          {/* Messages */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3 text-sm">
            {messages.length === 0 && (
              <>
                <div className="rounded-2xl rounded-tl-md bg-[hsl(var(--muted))] px-3.5 py-2.5 leading-relaxed">
                  {persona.greeting}
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  {SUGGESTED_QUESTIONS.map((q, i) => (
                    <button
                      key={i}
                      data-testid={`support-chat-suggestion-${i}`}
                      onClick={() => send(q)}
                      className="text-xs px-3 py-1.5 rounded-full border border-[hsl(var(--border))] bg-[hsl(var(--background))] hover:bg-[hsl(var(--muted))]"
                    >
                      {q}
                    </button>
                  ))}
                </div>
              </>
            )}

            {messages.map((m, i) => (
              <MessageBubble key={i} m={m} onEmail={openHandoff} />
            ))}

            {sending && (
              <div className="rounded-2xl rounded-tl-md bg-[hsl(var(--muted))] px-3.5 py-2.5 inline-flex items-center gap-2 text-[hsl(var(--muted-foreground))]" data-testid="support-chat-typing">
                <Loader2 size={14} className="animate-spin" />
                <span className="text-xs">Thinking…</span>
              </div>
            )}
          </div>

          {/* Composer */}
          <div className="border-t border-[hsl(var(--border))] p-3 pt-2.5">
            <div className="flex items-end gap-2">
              <textarea
                ref={inputRef}
                data-testid="support-chat-input"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); }
                }}
                rows={1}
                placeholder="Ask us anything…"
                className="flex-1 max-h-32 min-h-[38px] rounded-xl px-3 py-2 text-sm bg-[hsl(var(--background))] border border-[hsl(var(--input))] focus:outline-none focus:ring-2 focus:ring-[hsl(var(--ring))] resize-none"
              />
              <button
                data-testid="support-chat-send"
                onClick={() => send()}
                disabled={sending || !draft.trim()}
                className="h-10 w-10 grid place-items-center rounded-xl bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] disabled:opacity-40"
                aria-label="Send"
              >
                <Send size={16} />
              </button>
            </div>
            <div className="mt-2 flex items-center justify-between text-[10.5px] text-[hsl(var(--muted-foreground))]">
              <span>Prefer a human? <button onClick={openHandoff} className="text-[hsl(var(--accent))] hover:underline font-semibold">Send this chat to the team</button></span>
              {messages.length > 0 && (
                <button data-testid="support-chat-reset" onClick={reset} className="hover:text-[hsl(var(--foreground))]">Reset</button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Handoff modal */}
      {showHandoff && (
        <div className="fixed inset-0 z-[70] grid place-items-center p-4 bg-black/60" onClick={() => !handoffSending && setShowHandoff(false)} data-testid="support-handoff-modal">
          <div className="w-[min(94vw,440px)] rounded-2xl bg-[hsl(var(--card))] border border-[hsl(var(--border))] overflow-hidden" onClick={(e) => e.stopPropagation()}>
            {!handoffTicket ? (
              <div className="p-5">
                <div className="flex items-center gap-2">
                  <div className="h-9 w-9 grid place-items-center rounded-xl" style={{ background: "hsl(35 55% 92%)", color: "hsl(35 60% 30%)" }}>
                    <Mail size={16} />
                  </div>
                  <div>
                    <h3 className="font-display font-semibold text-base">Send this chat to a human</h3>
                    <p className="text-xs text-[hsl(var(--muted-foreground))]">We&apos;ll get back to you at the email you share below.</p>
                  </div>
                </div>

                <label className="mt-5 block text-xs font-semibold uppercase tracking-wide text-[hsl(var(--muted-foreground))]">Your email</label>
                <input
                  data-testid="support-handoff-email"
                  value={handoffEmail}
                  onChange={(e) => setHandoffEmail(e.target.value)}
                  placeholder="you@example.com"
                  type="email"
                  autoComplete="email"
                  className="mt-1.5 w-full h-11 rounded-xl px-3 bg-[hsl(var(--background))] border border-[hsl(var(--input))] focus:outline-none focus:ring-2 focus:ring-[hsl(var(--ring))] text-sm"
                />

                <label className="mt-4 block text-xs font-semibold uppercase tracking-wide text-[hsl(var(--muted-foreground))]">Anything else? (optional)</label>
                <textarea
                  data-testid="support-handoff-note"
                  value={handoffNote}
                  onChange={(e) => setHandoffNote(e.target.value)}
                  rows={3}
                  placeholder="e.g. I couldn't finish the checkout, or my invitations aren't landing."
                  className="mt-1.5 w-full rounded-xl px-3 py-2 bg-[hsl(var(--background))] border border-[hsl(var(--input))] focus:outline-none focus:ring-2 focus:ring-[hsl(var(--ring))] text-sm resize-none"
                />

                <div className="mt-3 rounded-lg bg-[hsl(var(--muted))] px-3 py-2 text-[11px] text-[hsl(var(--muted-foreground))] leading-snug">
                  We&apos;ll include the last {Math.min(messages.length, 30)} message{messages.length === 1 ? "" : "s"} of your chat so the team has context. Nothing sensitive is added.
                </div>

                <div className="mt-4 flex items-center gap-2">
                  <button onClick={() => setShowHandoff(false)} disabled={handoffSending}
                    className="flex-1 h-10 rounded-xl border border-[hsl(var(--border))] font-semibold text-sm hover:bg-[hsl(var(--muted))] disabled:opacity-60">Cancel</button>
                  <button data-testid="support-handoff-submit" onClick={submitHandoff} disabled={handoffSending || !handoffEmail.trim() || !/^\S+@\S+\.\S+$/.test(handoffEmail.trim())}
                    className="flex-1 h-10 rounded-xl bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] font-semibold text-sm disabled:opacity-40 inline-flex items-center justify-center gap-1.5">
                    {handoffSending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Send to team
                  </button>
                </div>
              </div>
            ) : (
              <div className="p-6 text-center">
                <div className="mx-auto h-11 w-11 grid place-items-center rounded-full" style={{ background: "hsl(35 55% 92%)", color: "hsl(35 60% 30%)" }}>
                  <Mail size={18} />
                </div>
                <h3 className="mt-3 font-display font-semibold text-base">We&apos;ll be in touch</h3>
                <p className="mt-1.5 text-sm text-[hsl(var(--muted-foreground))]">
                  Your chat is with the team. We&apos;ll reply to <strong>{handoffEmail}</strong> soon.
                </p>
                <div className="mt-3 inline-block rounded-lg bg-[hsl(var(--muted))] px-3 py-1.5 text-[11px] font-mono text-[hsl(var(--muted-foreground))]" data-testid="support-handoff-ticket">
                  Ticket #{String(handoffTicket).slice(0, 8)}
                </div>
                <button onClick={() => { setShowHandoff(false); setHandoffTicket(null); }}
                  className="mt-5 w-full h-10 rounded-xl bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] font-semibold text-sm">
                  Close
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}

function MessageBubble({ m, onEmail }) {
  if (m.role === "user") {
    return (
      <div className="flex justify-end" data-testid="support-chat-msg-user">
        <div className="max-w-[80%] rounded-2xl rounded-tr-md bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] px-3.5 py-2.5 leading-relaxed whitespace-pre-wrap">
          {m.content}
        </div>
      </div>
    );
  }
  return (
    <div className="max-w-[92%]" data-testid="support-chat-msg-assistant">
      <div className={`rounded-2xl rounded-tl-md px-3.5 py-2.5 leading-relaxed whitespace-pre-wrap ${m.error ? "bg-[hsl(var(--destructive))]/10 text-[hsl(var(--foreground))]" : "bg-[hsl(var(--muted))]"}`}>
        {m.content}
      </div>
      {m.suggest_email && (
        <button
          data-testid="support-chat-inline-email"
          onClick={onEmail}
          className="mt-1.5 inline-flex items-center gap-1 text-[11px] font-semibold text-[hsl(var(--accent))] hover:underline"
        >
          <Mail size={11} /> Send this chat to a human
        </button>
      )}
    </div>
  );
}
